import React from "react";
import { Card, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, LinearProgress } from "@mui/material";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import StatusChip from "../components/StatusChip";
import PlanBadge from "../components/PlanBadge";

const PLANS = [
  { name: "Starter", price: "$99/mo", seats: "50", api: "100K", storage: "10 GB", sla: "99.5%" },
  { name: "Growth", price: "$499/mo", seats: "500", api: "1M", storage: "100 GB", sla: "99.9%" },
  { name: "Business", price: "$1,499/mo", seats: "5,000", api: "10M", storage: "1 TB", sla: "99.95%" },
  { name: "Enterprise", price: "Custom", seats: "Unlimited", api: "Custom", storage: "Custom", sla: "99.99%" },
];

const TENANT_SUBS = [
  { tenant: "Acme Corp", plan: "Business", billing: "Annual", mrr: 1499, status: "Active", renewal: "Aug 2025" },
  { tenant: "Globex Industries", plan: "Enterprise", billing: "Annual", mrr: 4800, status: "Active", renewal: "Jun 2025" },
  { tenant: "Wayne Media", plan: "Growth", billing: "Monthly", mrr: 499, status: "Active", renewal: "Feb 2025" },
  { tenant: "Initech LLC", plan: "Starter", billing: "Monthly", mrr: 99, status: "Suspended", renewal: "—" },
  { tenant: "Umbrella Digital", plan: "Growth", billing: "Monthly", mrr: 0, status: "Trial", renewal: "Jan 2025" },
  { tenant: "Stark Streaming", plan: "Enterprise", billing: "Annual", mrr: 4800, status: "Active", renewal: "Mar 2025" },
  { tenant: "Hooli TV", plan: "Business", billing: "Monthly", mrr: 1499, status: "Offboarding", renewal: "—" },
];

const TRIAL_TENANTS = [
  { tenant: "Umbrella Digital", plan: "Growth", startDate: "Jan 5, 2025", daysLeft: 8, progress: 65, usage: "High", csm: "Meghana" },
  { tenant: "Pied Piper", plan: "Business", startDate: "Jul 1, 2025", daysLeft: 21, progress: 30, usage: "Medium", csm: "Vedant" },
  { tenant: "NextGen AI", plan: "Starter", startDate: "Jul 8, 2025", daysLeft: 28, progress: 7, usage: "Low", csm: "Unassigned" },
];

function Subscriptions() {
  const totalMrr = TENANT_SUBS.reduce((sum, t) => sum + t.mrr, 0);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={3}>
        {/* Page Header */}
        <MDBox mb={2.5}>
          <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
            <span style={{ color: "#5D6D7E", fontSize: 12 }}>Control Plane</span>
            <span style={{ color: "#BDC3C7" }}>›</span>
            <span style={{ color: "#1E3A5F", fontSize: 12, fontWeight: 600 }}>Subscriptions</span>
          </MDBox>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1E3A5F" }}>Subscription Management</h1>
        </MDBox>

        {/* Plan Comparison */}
        <Card sx={{ mb: 3 }}>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Subscription Plans
            </MDTypography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Plan</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Seats</TableCell>
                    <TableCell>API Calls/mo</TableCell>
                    <TableCell>Storage</TableCell>
                    <TableCell>SLA</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {PLANS.map((p) => (
                    <TableRow key={p.name}>
                      <TableCell>
                        <MDTypography variant="button" fontWeight="medium">{p.name}</MDTypography>
                      </TableCell>
                      <TableCell>{p.price}</TableCell>
                      <TableCell>{p.seats}</TableCell>
                      <TableCell>{p.api}</TableCell>
                      <TableCell>{p.storage}</TableCell>
                      <TableCell>{p.sla}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        </Card>

        {/* MRR Summary */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h3" color="info">${totalMrr.toLocaleString()}</MDTypography>
                <MDTypography variant="button" color="text">Monthly MRR</MDTypography>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h3" color="success">${(totalMrr * 12).toLocaleString()}</MDTypography>
                <MDTypography variant="button" color="text">Annualized ARR</MDTypography>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h3" color="warning">{TENANT_SUBS.length}</MDTypography>
                <MDTypography variant="button" color="text">Active Subscriptions</MDTypography>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* Tenant Subscriptions Table */}
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Tenant Subscriptions
            </MDTypography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tenant</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Billing</TableCell>
                    <TableCell align="right">MRR</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Next Renewal</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {TENANT_SUBS.map((t) => (
                    <TableRow key={t.tenant}>
                      <TableCell>
                        <MDTypography variant="button" fontWeight="medium">{t.tenant}</MDTypography>
                      </TableCell>
                      <TableCell>{t.plan}</TableCell>
                      <TableCell>{t.billing}</TableCell>
                      <TableCell align="right">${t.mrr.toLocaleString()}</TableCell>
                      <TableCell>
                        <Chip
                          label={t.status}
                          size="small"
                          color={
                            t.status === "Active" ? "success" :
                            t.status === "Trial" ? "info" :
                            t.status === "Suspended" ? "warning" : "default"
                          }
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{t.renewal}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        </Card>

        {/* Trial Tracker */}
        <Card sx={{ mt: 3 }}>
          <MDBox p={3}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h6" fontWeight="medium">
                Trial Tracker
              </MDTypography>
              <Chip label={`${TRIAL_TENANTS.length} in trial`} size="small" variant="outlined" color="info" />
            </MDBox>
            <div style={{ background: "#fff", border: "1px solid #E8EDF2", borderRadius: 10, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E8EDF2" }}>
                    {["Tenant", "Plan", "Start Date", "Days Left", "Activation Progress", "Usage Signal", "CSM", ""].map((h) => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", color: "#5D6D7E", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {TRIAL_TENANTS.map((t, i) => {
                    const usageColor = t.usage === "High" ? "#17A589" : t.usage === "Medium" ? "#E67E22" : "#C0392B";
                    return (
                      <tr key={t.tenant} style={{ borderBottom: "1px solid #F0F3F6", background: i % 2 === 0 ? "#fff" : "#FAFBFC" }}>
                        <td style={{ padding: "11px 14px", fontWeight: 600, color: "#1E3A5F" }}>{t.tenant}</td>
                        <td style={{ padding: "11px 14px" }}><PlanBadge plan={t.plan} /></td>
                        <td style={{ padding: "11px 14px", color: "#5D6D7E", fontSize: 12 }}>{t.startDate}</td>
                        <td style={{ padding: "11px 14px" }}>
                          <span style={{ fontWeight: 700, color: t.daysLeft <= 7 ? "#C0392B" : t.daysLeft <= 14 ? "#E67E22" : "#1E3A5F" }}>
                            {t.daysLeft} days
                          </span>
                        </td>
                        <td style={{ padding: "11px 14px", minWidth: 120 }}>
                          <MDBox display="flex" alignItems="center" gap={1}>
                            <LinearProgress
                              variant="determinate"
                              value={t.progress}
                              sx={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: "#E8EDF2", "& .MuiLinearProgress-bar": { borderRadius: 3, background: t.progress > 50 ? "#17A589" : "#E67E22" } }}
                            />
                            <span style={{ fontSize: 11, color: "#5D6D7E", minWidth: 28 }}>{t.progress}%</span>
                          </MDBox>
                        </td>
                        <td style={{ padding: "11px 14px" }}>
                          <span style={{ color: usageColor, fontWeight: 600, fontSize: 12 }}>{t.usage}</span>
                        </td>
                        <td style={{ padding: "11px 14px", color: "#5D6D7E", fontSize: 12 }}>{t.csm}</td>
                        <td style={{ padding: "11px 14px" }}>
                          <button style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: "#D5F5E3", color: "#0B6E4F", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                            Convert
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </MDBox>
        </Card>

        {/* Renewal Pipeline */}
        <Card sx={{ mt: 3 }}>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Renewal Pipeline (Next 90 Days)
            </MDTypography>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
              {[
                { tenant: "Globex Industries", plan: "Enterprise", renewalDate: "Jun 30, 2025", mrr: 4800, risk: "Low", healthScore: 88 },
                { tenant: "Wayne Media", plan: "Growth", renewalDate: "Feb 15, 2025", mrr: 499, risk: "Medium", healthScore: 76 },
                { tenant: "Stark Streaming", plan: "Enterprise", renewalDate: "Mar 1, 2025", mrr: 4800, risk: "Low", healthScore: 71 },
                { tenant: "Acme Corp", plan: "Business", renewalDate: "Aug 20, 2025", mrr: 1499, risk: "Low", healthScore: 92 },
              ].map((r) => {
                const riskColor = r.risk === "Low" ? "#17A589" : r.risk === "Medium" ? "#E67E22" : "#C0392B";
                const riskBg = r.risk === "Low" ? "#D5F5E3" : r.risk === "Medium" ? "#FDEBD0" : "#FADBD8";
                return (
                  <div key={r.tenant} style={{ border: "1px solid #E8EDF2", borderRadius: 10, padding: 16, background: "#fff" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                      <div>
                        <div style={{ fontWeight: 600, color: "#1E3A5F", fontSize: 14 }}>{r.tenant}</div>
                        <div style={{ fontSize: 12, color: "#5D6D7E", marginTop: 2 }}>{r.plan} · ${r.mrr.toLocaleString()}/mo</div>
                      </div>
                      <span style={{ background: riskBg, color: riskColor, padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
                        {r.risk} Risk
                      </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: 12, color: "#5D6D7E" }}>
                        <Icon sx={{ fontSize: "14px !important", verticalAlign: "middle", mr: 0.5 }}>event</Icon>
                        {r.renewalDate}
                      </div>
                      <div style={{ fontSize: 12, color: "#5D6D7E" }}>
                        Health: <strong style={{ color: r.healthScore >= 80 ? "#17A589" : r.healthScore >= 60 ? "#E67E22" : "#C0392B" }}>{r.healthScore}</strong>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Subscriptions;
